import {
  createKnowledgeInput,
  type KnowledgeItemDto,
  type KnowledgeKind,
  type KnowledgeScope,
  type KnowledgeSourceRefType,
} from '@fos/shared/contracts/knowledge';
import { KNOWLEDGE_KIND_LABELS, KNOWLEDGE_SCOPE_LABELS } from '@fos/shared/labels';
import { todayLocal } from '@fos/shared/time';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { checkFileBeforeUpload, uploadFile } from '../../../lib/files-api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { FilePickerInput } from '../../../ui/FilePickerInput';
import { Input } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { ProjectPicker } from '../../projects/components/ProjectPicker';
import { formatFileSize } from '../knowledge-format';
import { NoteEditor } from './NoteEditor';

const FIELD_KEYS = ['kind', 'title', 'scope', 'clientId', 'projectId', 'url', 'file', 'decidedOn', 'bodyMd'] as const;
type FieldKey = (typeof FIELD_KEYS)[number];
type Errors = Partial<Record<FieldKey, string>>;

const KIND_ORDER: KnowledgeKind[] = ['note', 'decision', 'link', 'file'];
const SCOPE_ORDER: KnowledgeScope[] = ['workspace', 'client', 'project'];
const SUBMIT_LABEL: Record<KnowledgeKind, string> = {
  note: 'Dodaj notatkę',
  decision: 'Dodaj decyzję',
  link: 'Dodaj link',
  file: 'Dodaj plik',
};
const SAVED_LABEL: Record<KnowledgeKind, string> = {
  note: 'Notatka zapisana',
  decision: 'Decyzja zapisana',
  link: 'Link zapisany',
  file: 'Plik zapisany',
};
const SCOPE_HINT: Record<KnowledgeScope, string> = {
  workspace: 'Widzą osoby z dostępem do wszystkich klientów i Ty.',
  client: 'Widzą osoby z dostępem do tego klienta.',
  project: 'Widzą osoby z dostępem do tego projektu.',
};
/** Atrapa id pliku do walidacji formularza przed wgraniem (prawdziwe id nadaje serwer). */
const PENDING_FILE_ID = '00000000-0000-4000-8000-000000000000';

function toErrors(issues: Array<{ path: string; message: string }>): Errors {
  const out: Errors = {};
  for (const issue of issues) {
    const head = issue.path.split('.')[0] ?? '';
    const key = (head === 'fileId' ? 'file' : head) as FieldKey;
    if ((FIELD_KEYS as readonly string[]).includes(key) && !out[key]) out[key] = issue.message;
  }
  return out;
}

export interface NewNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** wstępny klient (profil klienta) */
  defaultClientId?: string | null;
  /** wstępny projekt (szczegóły projektu); wyznacza klienta */
  defaultProjectId?: string | null;
  defaultKind?: KnowledgeKind;
  /** źródło (np. decyzja z notatki z ustaleniami: `{ type: 'knowledge_item', id }`) */
  sourceRef?: { type: KnowledgeSourceRefType; id: string } | null;
  /** po zapisie; domyślnie przejście do rekordu (/wiedza/:id) */
  onCreated?: (item: KnowledgeItemDto) => void;
}

/**
 * Nowy rekord wiedzy (MVP §8): notatka Markdown, decyzja (z datą), link albo plik; zakres firma / klient / projekt
 * (projekt wyznacza klienta). Plik jest najpierw wgrywany (REST, typ sprawdzany po treści), potem powstaje rekord.
 */
export function NewNoteDialog({
  open,
  onOpenChange,
  defaultClientId = null,
  defaultProjectId = null,
  defaultKind = 'note',
  sourceRef = null,
  onCreated,
}: NewNoteDialogProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const formRef = useRef<HTMLFormElement>(null);
  const initialScope: KnowledgeScope = defaultProjectId ? 'project' : defaultClientId ? 'client' : 'workspace';
  const [kind, setKind] = useState<KnowledgeKind>(defaultKind);
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<KnowledgeScope>(initialScope);
  const [clientId, setClientId] = useState<string | null>(defaultClientId);
  const [projectId, setProjectId] = useState<string | null>(defaultProjectId);
  const [decidedOn, setDecidedOn] = useState(() => todayLocal());
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [bodyMd, setBodyMd] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useFocusFirstInvalid(formRef, errors);

  // nowe otwarcie = czysty formularz z wartościami domyślnymi
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setKind(defaultKind);
    setTitle('');
    setScope(initialScope);
    setClientId(defaultClientId);
    setProjectId(defaultProjectId);
    setDecidedOn(todayLocal());
    setUrl('');
    setFile(null);
    setBodyMd('');
    setErrors({});
    setFormError(null);
  }, [open]);

  const create = trpc.knowledge.create.useMutation();
  const archiveFile = trpc.knowledge.archiveFile.useMutation();

  const buildInput = (fileId: string | null) => ({
    scope,
    clientId: scope === 'workspace' ? null : clientId,
    projectId: scope === 'project' ? projectId : null,
    kind,
    title: title.trim(),
    bodyMd,
    url: kind === 'link' ? url.trim() || null : null,
    fileId: kind === 'file' ? fileId : null,
    decidedOn: kind === 'decision' ? decidedOn || null : null,
    sourceRef,
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const local: Errors = {};
    const parsed = createKnowledgeInput.safeParse(buildInput(file ? PENDING_FILE_ID : null));
    if (!parsed.success) {
      Object.assign(local, toErrors(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }))));
    }
    if (kind === 'file') {
      const fileProblem = file ? checkFileBeforeUpload(file) : 'Wybierz plik.';
      if (fileProblem) local.file = fileProblem;
    }
    if (Object.keys(local).length > 0) {
      setErrors(local);
      return;
    }
    setErrors({});
    setBusy(true);
    let uploadedId: string | null = null;
    try {
      if (kind === 'file' && file) {
        try {
          const uploaded = await uploadFile(file, {
            clientId: scope === 'workspace' ? null : clientId,
            projectId: scope === 'project' ? projectId : null,
          });
          uploadedId = uploaded.id;
        } catch (err) {
          const issues = validationIssues(err);
          setErrors({ file: issues[0]?.message ?? errorMessage(err) });
          return;
        }
      }
      const item = await create.mutateAsync(buildInput(uploadedId));
      void utils.knowledge.list.invalidate();
      void utils.knowledge.listDecisions.invalidate();
      toast.success(SAVED_LABEL[item.kind], { description: item.title });
      onOpenChange(false);
      if (onCreated) onCreated(item);
      else void navigate(`/wiedza/${item.id}`);
    } catch (err) {
      // rekord nie powstał: wgrany plik do archiwum (bez osieroconych plików na liście)
      if (uploadedId) archiveFile.mutate({ id: uploadedId });
      const fieldErrors = toErrors(validationIssues(err));
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else if (appErrorCode(err) === 'NOT_FOUND')
        setFormError('Klient albo projekt nie istnieje lub jest poza Twoim zakresem.');
      else setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !busy && onOpenChange(next)}
      width="l"
      title="Nowa notatka"
      className={FULLSCREEN_ON_PHONE}
      description="Notatka, decyzja, link albo plik. Każda zmiana treści zapisuje nową wersję."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="new-note-form" loading={busy}>
            {SUBMIT_LABEL[kind]}
          </Button>
        </>
      }
    >
      <form
        id="new-note-form"
        ref={formRef}
        onSubmit={(e) => void submit(e)}
        noValidate
        className="flex flex-col gap-4"
      >
        {formError && <FormMessage>{formError}</FormMessage>}
        <Field label="Rodzaj" error={errors.kind}>
          <div className="-mx-1 overflow-x-auto px-1">
            <Segmented
              aria-label="Rodzaj"
              value={kind}
              onValueChange={setKind}
              options={KIND_ORDER.map((k) => ({ value: k, label: KNOWLEDGE_KIND_LABELS[k] }))}
            />
          </div>
        </Field>
        <Field label="Tytuł" error={errors.title}>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={300}
            placeholder={
              kind === 'decision' ? 'Np. Formularz zostaje na /kontakt, bez CAPTCHA' : 'Np. Ustalenia z warsztatu'
            }
            autoFocus
          />
        </Field>
        <Field label="Dotyczy" hint={SCOPE_HINT[scope]} error={errors.scope}>
          <Segmented
            aria-label="Dotyczy"
            value={scope}
            onValueChange={(next) => {
              setScope(next);
              setErrors((e) => ({ ...e, scope: undefined, clientId: undefined, projectId: undefined }));
            }}
            options={SCOPE_ORDER.map((s) => ({ value: s, label: KNOWLEDGE_SCOPE_LABELS[s] }))}
          />
        </Field>
        {scope === 'client' && (
          <Field label="Klient" error={errors.clientId}>
            <ClientPicker value={clientId} onChange={setClientId} excludePartial />
          </Field>
        )}
        {scope === 'project' && (
          <Field label="Projekt" error={errors.projectId ?? errors.clientId}>
            <ProjectPicker
              value={projectId}
              allowEmpty={false}
              onValueChange={(id, project) => {
                setProjectId(id);
                setClientId(project?.client?.id ?? null);
              }}
            />
          </Field>
        )}
        {kind === 'decision' && (
          <Field label="Data decyzji" error={errors.decidedOn}>
            <DateInput value={decidedOn} onValueChange={setDecidedOn} />
          </Field>
        )}
        {kind === 'link' && (
          <Field label="Adres" error={errors.url}>
            <Input
              type="url"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
            />
          </Field>
        )}
        {kind === 'file' && (
          <Field
            label="Plik"
            hint={
              file
                ? `${file.name} · ${formatFileSize(file.size)}`
                : 'PDF, PNG, JPG, WebP, TXT, MD, CSV, DOCX, XLSX, ZIP · do 25 MB'
            }
            error={errors.file}
          >
            <FilePickerInput
              onFile={(next) => {
                setFile(next);
                setErrors((prev) => ({ ...prev, file: undefined }));
              }}
            />
          </Field>
        )}
        <NoteEditor
          label={kind === 'note' ? 'Treść' : 'Opis (opcjonalnie)'}
          value={bodyMd}
          onChange={setBodyMd}
          rows={kind === 'note' ? 10 : 4}
          error={errors.bodyMd}
        />
      </form>
    </Dialog>
  );
}
